﻿import { Link, useLocation } from 'react-router-dom';
import { DT, DS2 } from '../../theme/designTokens';
import { useAppShell } from '../../context/AppShellContext';
import { NAV_ITEMS } from '../../config/navigation';
import { ASSETS } from '../../config/assets';
import type { NavItem } from '../../config/navigation';
import { useFriends } from '../../context/FriendContext';
import { useAuth } from '../../context/AuthContext';
import { useIdentity, resolveDisplayName } from '../../hooks/useIdentity';
import { useTranslation } from '../../i18n/react';
import { PlayerAvatar } from '../ui/PlayerAvatar';

// Desktop nav order matches old deployed sidebar:
// Home · Play Online · Play vs AI (soon) · Friends (soon) · Stats · Leaderboard (soon) · Profile
const TOP_NAV_ITEMS: NavItem[] = NAV_ITEMS;

interface Props {
  /**
   * DS2 mobile chrome for routes on AppShell's SIDEBAR_SHELL_ROUTES bridge:
   * solid DS2 surface + gold edge (mobile counterpart of the Sidebar), logo
   * links to the route root. Legacy routes render the original translucent
   * bar unchanged.
   */
  ds2?: boolean;
}

export function TopNav({ ds2 = false }: Props) {
  const { openDrawer } = useAppShell();
  const { pathname } = useLocation();
  const { pendingCount, unreadMessageCount } = useFriends();
  const { t } = useTranslation('navigation');
  const { t: tc } = useTranslation('common');
  const { t: tm } = useTranslation('messages');
  // Screen-reader users get the same information the visual dot conveys —
  // appended to the existing "Open navigation" label rather than replacing
  // it, so the control's primary purpose stays first.
  const openNavLabel = unreadMessageCount > 0
    ? `${t('aria.openNav')} — ${tm('chat.unreadAria', { count: unreadMessageCount })}`
    : t('aria.openNav');
  const { isLoggedIn, user, devDisplayName } = useAuth();
  const identity = useIdentity();
  const isActive = (path: string) =>
    pathname === path || pathname.startsWith(path + '/');

  // Player Identity V1: the profile button shows the user's real identity
  // (preset avatar or display-name initial) inside the existing 38px circle
  // instead of the generic person silhouette. Logged-out chrome unchanged.
  const profileAvatar = isLoggedIn ? (
    <PlayerAvatar
      avatarKey={identity?.avatarKey}
      name={resolveDisplayName(identity, user, devDisplayName || 'Player')}
      size={36}
      loading="eager"
      initialStyle={{ background: 'transparent', border: 'none' }}
    />
  ) : null;

  return (
    <nav
      style={{
        position:     'fixed',
        top: 0, left: 0, right: 0,
        // Grows by the status-bar/notch inset so the bar's background still
        // reaches the true top of the screen on native (Android/iOS); the
        // inset is absorbed as top padding (box-sizing:border-box, see
        // designTokens.ts topNavHeightCss) so inner 100%-height content
        // keeps its original topNavHeight box, just pushed down.
        height:       DT.layout.topNavHeightCss,
        paddingTop:   DT.layout.safeAreaTop,
        boxSizing:    'border-box',
        // Legacy — Design Master source: header bg-background/70 + backdrop-blur-md
        // DS2 — solid Sidebar surface with the same #CB9719 gold edge, no blur
        background:         ds2 ? DS2.color.surface : 'rgba(11,11,11,0.70)',
        backdropFilter:     ds2 ? undefined : 'blur(12px)',
        WebkitBackdropFilter: ds2 ? undefined : 'blur(12px)',
        borderBottom: ds2 ? '1px solid #CB9719' : `1px solid ${DT.color.border}`,
        zIndex:       50,
      }}
    >
      {/* ── DS2 mobile layout (Figma GET STARTED mockup): gold-ringed hamburger
             circle · centered horizontal logo · Sign Up + Log In pills ── */}
      {ds2 && (
        <div
          className="flex md:hidden"
          style={{
            position:       'relative',
            height:         '100%',
            alignItems:     'center',
            justifyContent: 'space-between',
            padding:        '0 12px',
          }}
        >
          {/* Hamburger — circle with gold ring */}
          <button
            onClick={openDrawer}
            aria-label={openNavLabel}
            style={{
              position:       'relative',
              width:          40,
              height:         40,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              background:     'transparent',
              border:         `1px solid ${DS2.color.gold}`,
              borderRadius:   '50%',
              cursor:         'pointer',
              color:          DS2.color.textBright,
              flexShrink:     0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <HamburgerSVG />
            <UnreadDot show={unreadMessageCount > 0} ringColor={DS2.color.surface} />
          </button>

          {/* Logo — horizontal lockup, centered in the space between the
              hamburger and the auth pills; shrinks on narrow phones so it
              never overlaps them (the Figma mockup is a wider canvas). */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', padding: '0 8px' }}>
            <Link to="/" style={{ display: 'flex', minWidth: 0, textDecoration: 'none' }}>
              <img
                src={ASSETS.brand.LOGO_HORIZONTAL}
                alt="The Domino Game"
                style={{ height: 40, maxWidth: '100%', width: 'auto', objectFit: 'contain' }}
                draggable={false}
              />
            </Link>
          </div>

          {/* Right: auth pills (logged out) or profile avatar (logged in) */}
          {isLoggedIn ? (
            <Link
              to="/settings"
              aria-label={t('aria.settings')}
              style={{
                width: 38, height: 38, borderRadius: '50%',
                background: '#131313', border: `1px solid ${DS2.color.gold}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: DS2.color.textBright, textDecoration: 'none', flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {profileAvatar ?? <PersonSVG />}
            </Link>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Link
                to="/signup"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '7px 14px', borderRadius: 10,
                  background: DS2.gradient.gold, color: '#FFFFFF',
                  fontSize: 13, fontWeight: 500, fontFamily: DS2.font.body,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {tc('action.signUp')}
              </Link>
              <Link
                to="/login"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '7px 14px', borderRadius: 10,
                  background: '#131313', border: `1px solid ${DS2.color.gold}`,
                  color: '#FFFFFF',
                  fontSize: 13, fontWeight: 500, fontFamily: DS2.font.body,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {tc('action.logIn')}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ── Legacy mobile layout: 3-col grid, full-bleed (no contentPadding) ── */}
      {/* grid-cols: 48px hamburger | 1fr logo centered | 48px avatar        */}
      {/* No outer padding so the full viewport width is the grid boundary.  */}
      {/* At 375px: center = 375 - 48 - 48 = 279px; logo h=40 → 277px ✓   */}
      {!ds2 && (
      <div
        className="grid md:hidden"
        style={{
          height:              '100%',
          gridTemplateColumns: '48px 1fr 48px',
          alignItems:          'center',
        }}
      >
        {/* Col 1: hamburger */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={openDrawer}
            aria-label={openNavLabel}
            style={{
              position:  'relative',
              width:     40,
              height:    40,
              display:   'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:   'transparent',
              border:       'none',
              cursor:       'pointer',
              color:        ds2 ? DS2.color.textBright : DT.color.textSecondary,
              borderRadius: DT.radius.sm,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <HamburgerSVG />
            <UnreadDot show={unreadMessageCount > 0} ringColor="#0B0B0B" />
          </button>
        </div>

        {/* Col 2: logo, centered in column */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Link to={ds2 ? '/' : '/home'} style={{ display: 'flex', textDecoration: 'none' }}>
            <img
              src={ASSETS.brand.LOGO_HORIZONTAL}
              alt="The Domino Game"
              style={{ height: 40, width: 'auto', objectFit: 'contain' }}
              draggable={false}
            />
          </Link>
        </div>

        {/* Col 3: avatar */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Link
            to="/settings"
            aria-label={t('aria.settings')}
            style={{
              width:          38,
              height:         38,
              borderRadius:   '50%',
              background:     DT.color.cardBg,
              border:         `1px solid ${DT.color.border}`,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              color:          DT.color.textSecondary,
              textDecoration: 'none',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {profileAvatar ?? <PersonSVG />}
          </Link>
        </div>
      </div>
      )}

      {/* ── Desktop layout: flex row with contentPadding and nav links ── */}
      <div
        className="hidden md:flex"
        style={{
          maxWidth:      DT.layout.contentMaxWidth,
          margin:        '0 auto',
          paddingLeft:   DT.layout.contentPadding,
          paddingRight:  DT.layout.contentPadding,
          height:        '100%',
          alignItems:    'center',
          gap:           8,
        }}
      >
        {/* Logo */}
        <Link
          to="/home"
          style={{
            display:        'flex',
            alignItems:     'center',
            textDecoration: 'none',
            flexShrink:     0,
          }}
        >
          <img
            src={ASSETS.brand.LOGO_HORIZONTAL}
            alt="The Domino Game"
            style={{ height: 44, width: 'auto', objectFit: 'contain' }}
            draggable={false}
          />
        </Link>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginLeft: 16 }}>
          {TOP_NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              item={item}
              active={isActive(item.path)}
              badge={item.path === '/friends' ? pendingCount : undefined}
            />
          ))}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Avatar */}
        <Link
          to="/settings"
          aria-label={t('aria.settings')}
          style={{
            width:          38,
            height:         38,
            borderRadius:   '50%',
            background:     DT.color.cardBg,
            border:         `1px solid ${DT.color.border}`,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            flexShrink:     0,
            color:          DT.color.textSecondary,
            textDecoration: 'none',
            WebkitTapHighlightColor: 'transparent',
            transition:     `border-color ${DT.motion.normal}ms`,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = DT.hover.avatarBorder; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = DT.color.border; }}
        >
          {profileAvatar ?? <PersonSVG />}
        </Link>
      </div>
    </nav>
  );
}

function NavLink({ item, active, badge }: { item: NavItem; active: boolean; badge?: number }) {
  const { t } = useTranslation('navigation');
  return (
    <Link
      to={item.path}
      style={{
        display:        'flex',
        alignItems:     'center',
        gap:            6,
        padding:        '8px 14px',
        borderRadius:   DT.radius.sm,
        textDecoration: 'none',
        // Design Master source: hover:text-gold — color only, no background highlight
        // Active state retained for app navigation clarity (SBGDS extension).
        color:           active ? DT.color.gold : DT.color.textSecondary,
        fontSize:       DT.type.navItem.size,
        fontWeight:     active ? 600 : 500,
        fontFamily:     DT.font.sans,
        transition:     `color ${DT.motion.fast}ms, background ${DT.motion.fast}ms`,
        whiteSpace:     'nowrap',
      }}
      // Design Master source: nav <a> class="transition hover:text-gold" — color shift only, no bg
      onMouseEnter={e => { if (!active) (e.currentTarget as HTMLAnchorElement).style.color = DT.hover.navTextColor; }}
      onMouseLeave={e => { if (!active) (e.currentTarget as HTMLAnchorElement).style.color = DT.color.textSecondary; }}
    >
      {t(`items.${item.labelKey}`)}
      {(badge != null && badge > 0) && (
        <span style={{
          backgroundColor: DT.color.gold, color: DT.color.goldOnGold,
          fontSize: 10, fontWeight: 800, fontFamily: DT.font.sans,
          minWidth: 16, height: 16, borderRadius: 999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px', lineHeight: 1,
        }}>
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      {item.soon && <SoonBadge />}
    </Link>
  );
}

function SoonBadge() {
  // Compact 9px badge — es-419 uses the approved short form "Pronto".
  const { t } = useTranslation('navigation');
  return (
    <span
      style={{
        fontSize:      9,
        fontWeight:    700,
        fontFamily:    'Barlow, sans-serif',
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color:         DT.color.textSecondary,
        background:    'rgba(255,255,255,0.08)',
        borderRadius:  4,
        padding:       '2px 5px',
        lineHeight:    1,
      }}
    >
      {t('badge.soon')}
    </span>
  );
}

/**
 * Mobile unread-message hint on the closed hamburger — deliberately a plain
 * dot, not a count: this is the "something is waiting" signal (the app-icon
 * pattern), distinct from the exact numbered badges shown once the drawer's
 * CHAT entry (or the desktop dock) is actually open. Desktop never renders
 * this — the persistent Sidebar's CHAT button already carries the real
 * count at all times, so a second aggregate hint would be redundant.
 */
function UnreadDot({ show, ringColor }: { show: boolean; ringColor: string }) {
  if (!show) return null;
  return (
    <span
      aria-hidden="true"
      style={{
        position:     'absolute',
        top:          1,
        right:        1,
        width:        11,
        height:       11,
        borderRadius: '50%',
        background:   '#E05252',
        border:       `2px solid ${ringColor}`,
      }}
    />
  );
}

function HamburgerSVG() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6h18M3 12h18M3 18h18"
        stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"
      />
    </svg>
  );
}

function PersonSVG() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4 20c0-3.87 3.58-7 8-7s8 3.13 8 7"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
      />
    </svg>
  );
}

